import { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setIsCollapsed, setIsOverlay, setIsSmallScreen } from '../../../redux/features/layout/layoutSlice';

const SMALL_SCREEN_BREAKPOINT = 1000;

export interface ChatListLayout {
    isCollapsed: boolean;
    isSmallScreen: boolean;
    isOverlay: boolean;
    handleExpand: () => void;
}

const useChatListLayout = (): ChatListLayout => {
    const dispatch = useAppDispatch();

    const isCollapsed = useAppSelector((state) => state.layout.isCollapsed);
    const isSmallScreen = useAppSelector((state) => state.layout.isSmallScreen);
    const isOverlay = useAppSelector((state) => state.layout.isOverlay);

    // --- Responsive: initialise on mount ---
    useEffect(() => {
        const small = window.innerWidth <= SMALL_SCREEN_BREAKPOINT;
        dispatch(setIsSmallScreen(small));
        dispatch(setIsCollapsed(small));
    }, [dispatch]);

    // --- Responsive: window resize listener ---
    useEffect(() => {
        const handleResize = () => {
            const nowSmall = window.innerWidth <= SMALL_SCREEN_BREAKPOINT;

            // Transitioning large → small
            if (nowSmall && !isSmallScreen) {
                dispatch(setIsCollapsed(true));
                dispatch(setIsSmallScreen(nowSmall));
                dispatch(setIsOverlay(false));
                return;
            }

            // Transitioning small → large
            if (!nowSmall && isSmallScreen) {
                dispatch(setIsSmallScreen(false));
                dispatch(setIsCollapsed(false));
                dispatch(setIsOverlay(false));
                return;
            }

            // Still small, expanded but not overlay → force overlay
            if (isSmallScreen && !isCollapsed && !isOverlay) {
                dispatch(setIsOverlay(true));
            }

            // Large or collapsed → make sure overlay is off
            if (!isSmallScreen || isCollapsed) {
                dispatch(setIsOverlay(false));
            }
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [dispatch, isCollapsed, isOverlay, isSmallScreen]);

    // --- Toggle handler ---
    const handleExpand = useCallback(() => {
        if (isSmallScreen) {
            dispatch(setIsOverlay(!isOverlay));
            dispatch(setIsCollapsed(!isCollapsed));
        } else {
            dispatch(setIsCollapsed(!isCollapsed));
        }
    }, [dispatch, isCollapsed, isOverlay, isSmallScreen]);

    return { isCollapsed, isSmallScreen, isOverlay, handleExpand };
};

export default useChatListLayout;
